import { getSupabaseClient } from '../../lib/supabase'
import type { Property, PropertyFormValues } from './property-types'

export interface OrganizationAccess {
  organizationId: string
  organizationName: string
  role: 'owner' | 'admin' | 'editor' | 'viewer'
}

export async function getOrganizationAccess(): Promise<OrganizationAccess> {
  const supabase = getSupabaseClient()
  const { error: invitationError } = await supabase.rpc('accept_my_invitations')
  if (invitationError && invitationError.code !== 'PGRST202') throw invitationError
  const { data: membership, error: membershipError } = await supabase
    .from('organization_members')
    .select('organization_id, role')
    .limit(1)
    .maybeSingle()

  if (membershipError) throw membershipError
  if (!membership) throw new Error('Your account is not assigned to a PropertyReady organization.')

  const { data: organization, error: organizationError } = await supabase
    .from('organizations')
    .select('name')
    .eq('id', membership.organization_id)
    .single()

  if (organizationError) throw organizationError
  return {
    organizationId: membership.organization_id,
    organizationName: organization.name,
    role: membership.role,
  }
}

export async function listProperties(organizationId: string): Promise<Property[]> {
  const { data, error } = await getSupabaseClient()
    .from('properties')
    .select('*')
    .eq('organization_id', organizationId)
    .order('updated_at', { ascending: false })

  if (error) throw error
  return data
}

export async function getProperty(propertyId: string, organizationId: string): Promise<Property> {
  const { data, error } = await getSupabaseClient()
    .from('properties')
    .select('*')
    .eq('id', propertyId)
    .eq('organization_id', organizationId)
    .single()

  if (error) throw error
  return data
}

function toDatabaseValues(values: PropertyFormValues) {
  return {
    name: values.name.trim(),
    address_line_1: values.addressLine1.trim(),
    city: values.city.trim(),
    region: values.region.trim(),
    postal_code: values.postalCode.trim(),
    country_code: values.countryCode.trim().toUpperCase(),
    status: values.status,
  }
}

export async function createProperty(
  organizationId: string,
  userId: string,
  values: PropertyFormValues,
): Promise<Property> {
  const { data, error } = await getSupabaseClient()
    .from('properties')
    .insert({ organization_id: organizationId, created_by: userId, ...toDatabaseValues(values) })
    .select('*')
    .single()

  if (error) throw error
  return data
}

export async function updateProperty(propertyId: string, values: PropertyFormValues): Promise<Property> {
  const { data, error } = await getSupabaseClient()
    .from('properties')
    .update(toDatabaseValues(values))
    .eq('id', propertyId)
    .select('*')
    .single()

  if (error) throw error
  return data
}

export async function setPropertyStatus(propertyId: string, status: Property['status']): Promise<Property> {
  const { data, error } = await getSupabaseClient()
    .from('properties')
    .update({ status })
    .eq('id', propertyId)
    .select('*')
    .single()

  if (error) throw error
  return data
}
